const UNPLACED_TERM_VALUE = 'unplaced';

const TERM_FILTER_PRESETS = [
  { value: 'q1', display: 'Q1', terms: ['1'] },
  { value: 'q2', display: 'Q2', terms: ['2'] },
  { value: 'q3', display: 'Q3', terms: ['3'] },
  { value: 'q4', display: 'Q4', terms: ['4'] },
  { value: 's1', display: 'S1', terms: ['1', '2'] },
  { value: 's2', display: 'S2', terms: ['3', '4'] },
  { value: 'full_year', display: 'Full Year', terms: ['1', '2', '3', '4'] },
  { value: 'cycle', display: 'Cycle', terms: ['1', '2', '3', '4', '5', '6'] },
];

export const SECTION_ALIGNMENT_LANES = {
  full_year: [
    { key: 'full_year', display: 'Full Year', terms: ['1', '2', '3', '4'] },
  ],
  semester: [
    { key: 's1', display: 'S1', terms: ['1', '2'] },
    { key: 's2', display: 'S2', terms: ['3', '4'] },
  ],
  quarter: [
    { key: 'q1', display: 'Q1', terms: ['1'] },
    { key: 'q2', display: 'Q2', terms: ['2'] },
    { key: 'q3', display: 'Q3', terms: ['3'] },
    { key: 'q4', display: 'Q4', terms: ['4'] },
  ],
  cycle: [
    { key: 'c1', display: 'C1', terms: ['1'] },
    { key: 'c2', display: 'C2', terms: ['2'] },
    { key: 'c3', display: 'C3', terms: ['3'] },
    { key: 'c4', display: 'C4', terms: ['4'] },
    { key: 'c5', display: 'C5', terms: ['5'] },
    { key: 'c6', display: 'C6', terms: ['6'] },
  ],
};

const termSetsMatch = (terms_a, terms_b) => (
  terms_a.length === terms_b.length &&
  terms_a.every((term, index) => `${term}` === `${terms_b[index]}`)
);

export const getSectionTermValues = (section_info = {}) => {
  const quarter_days = Array.isArray(section_info?.quarter_days) ? section_info.quarter_days : [];
  return Array.from(new Set(quarter_days.map(quarter_day => `${quarter_day?.quarter || ''}`).filter(Boolean)))
    .sort((term_a, term_b) => parseInt(term_a, 10) - parseInt(term_b, 10));
};

export const getTermDisplayLabel = (term) => `${['5', '6'].includes(`${term}`) ? 'C' : 'Q'}${term}`;

export const getSectionTermFilterInfo = (section_info = {}) => {
  const selected_terms = getSectionTermValues(section_info);

  if(selected_terms.length === 0) {
    return { value: UNPLACED_TERM_VALUE, display: 'Unplaced', selected_terms };
  }

  const preset = TERM_FILTER_PRESETS.find(term_preset => termSetsMatch(term_preset.terms, selected_terms));
  if(preset) return { ...preset, selected_terms };

  return {
    value: `custom:${selected_terms.join(',')}`,
    display: selected_terms.map(getTermDisplayLabel).join('/'),
    selected_terms,
  };
};

export const getSectionAlignmentLaneInfo = (section_info = {}) => {
  const selected_terms = getSectionTermValues(section_info);
  if(selected_terms.length === 0) return null;

  const has_cycle_terms = selected_terms.some(term => ['5', '6'].includes(`${term}`));
  const lane_groups = has_cycle_terms
    ? [SECTION_ALIGNMENT_LANES.cycle]
    : [SECTION_ALIGNMENT_LANES.full_year, SECTION_ALIGNMENT_LANES.semester, SECTION_ALIGNMENT_LANES.quarter];

  for(const lane_group of lane_groups) {
    const lane_info = lane_group.find(lane => termSetsMatch(lane.terms, selected_terms));
    if(lane_info) return lane_info;
  }

  if(!has_cycle_terms && selected_terms.length >= 1 && selected_terms.length <= 4) {
    return {
      key: `custom:${selected_terms.join('|')}`,
      display: selected_terms.map(getTermDisplayLabel).join('/'),
      terms: selected_terms,
    };
  }

  return null;
};

const getAlignmentLaneSortValue = (lane_info = {}) => {
  const terms = Array.isArray(lane_info.terms) ? lane_info.terms : [];
  const first_term = parseInt(terms[0] || '99', 10);
  const duration_rank = {
    4: 0,
    3: 1,
    2: 2,
    1: 3,
  };
  const duration_value = Object.prototype.hasOwnProperty.call(duration_rank, terms.length)
    ? duration_rank[terms.length]
    : 4;

  return ((Number.isFinite(first_term) ? first_term : 99) * 10) + duration_value;
};

const getAlignmentLaneGroupKey = (lane_info = {}) => {
  const terms = Array.isArray(lane_info.terms) ? lane_info.terms : [];
  const has_cycle_terms = terms.some(term => ['5', '6'].includes(`${term}`));
  return `${has_cycle_terms ? 'cycle' : 'standard'}:${terms.length}`;
};

export const getCompactSectionAlignmentTemplate = (section_placements = []) => {
  const lane_info_by_key = new Map();

  (Array.isArray(section_placements) ? section_placements : []).forEach(section_placement => {
    const section_info = section_placement?.section || section_placement;
    const lane_info = getSectionAlignmentLaneInfo(section_info);
    if(!lane_info) return;

    lane_info_by_key.set(lane_info.key, lane_info);
  });

  const present_lanes = Array.from(lane_info_by_key.values())
    .sort((left_lane, right_lane) => {
      const sort_difference = getAlignmentLaneSortValue(left_lane) - getAlignmentLaneSortValue(right_lane);
      if(sort_difference !== 0) return sort_difference;
      return `${left_lane.key}`.localeCompare(`${right_lane.key}`);
    });

  const next_lane_index_by_group = new Map();

  const lanes = present_lanes
    .map((lane_info) => {
      const group_key = getAlignmentLaneGroupKey(lane_info);
      const lane_index = next_lane_index_by_group.get(group_key) || 0;
      next_lane_index_by_group.set(group_key, lane_index + 1);

      return {
        ...lane_info,
        groupKey: group_key,
        laneIndex: lane_index,
        orderValue: getAlignmentLaneSortValue(lane_info),
      };
    })
    .sort((left_lane, right_lane) => {
      return getAlignmentLaneSortValue(left_lane) - getAlignmentLaneSortValue(right_lane);
    });

  return {
    lanes,
    lanesByKey: lanes.reduce((lookup, lane_info) => {
      lookup[lane_info.key] = lane_info;
      return lookup;
    }, {}),
  };
};

export const getSectionAlignmentRenderItems = (sections = [], section_alignment_template = null) => {
  const lanes_by_key = section_alignment_template?.lanesByKey || {};
  const has_alignment_lanes = Object.keys(lanes_by_key).length > 0;

  if(!has_alignment_lanes) {
    return (Array.isArray(sections) ? sections : []).map(section_info => ({
      type: 'section',
      section_info,
    }));
  }

  const sections_by_group_key = new Map();
  const group_order_values = new Map();
  const unaligned_sections = [];

  (Array.isArray(sections) ? sections : []).forEach(section_info => {
    const lane_info = getSectionAlignmentLaneInfo(section_info);
    const aligned_lane_info = lane_info ? lanes_by_key[lane_info.key] : null;

    if(!aligned_lane_info || aligned_lane_info.laneIndex === undefined) {
      unaligned_sections.push(section_info);
      return;
    }

    const group_key = aligned_lane_info.groupKey || 'default';
    const group_sections_by_lane_index = sections_by_group_key.get(group_key) || new Map();
    const lane_index = aligned_lane_info.laneIndex || 0;
    const lane_sections = group_sections_by_lane_index.get(lane_index) || [];
    lane_sections.push(section_info);
    group_sections_by_lane_index.set(lane_index, lane_sections);
    sections_by_group_key.set(group_key, group_sections_by_lane_index);

    const order_value = Number.isFinite(aligned_lane_info.orderValue)
      ? aligned_lane_info.orderValue
      : 999;
    const current_group_order_value = group_order_values.has(group_key)
      ? group_order_values.get(group_key)
      : 999;
    if(order_value < current_group_order_value) {
      group_order_values.set(group_key, order_value);
    }
  });

  const render_items = [];

  Array.from(sections_by_group_key.entries())
    .sort(([left_group_key], [right_group_key]) => (
      (group_order_values.get(left_group_key) ?? 999) - (group_order_values.get(right_group_key) ?? 999)
    ))
    .forEach(([group_key, group_sections_by_lane_index], group_index) => {
      const occupied_lane_indexes = Array.from(group_sections_by_lane_index.keys())
        .filter(lane_index => Number.isInteger(lane_index))
        .sort((left_index, right_index) => left_index - right_index);
      const min_occupied_lane_index = occupied_lane_indexes.length > 0
        ? occupied_lane_indexes[0]
        : 0;
      const max_occupied_lane_index = occupied_lane_indexes.length > 0
        ? occupied_lane_indexes[occupied_lane_indexes.length - 1]
        : -1;
      const first_lane_index_to_render = group_index === 0
        ? 0
        : min_occupied_lane_index;

      for(let lane_index = first_lane_index_to_render; lane_index <= max_occupied_lane_index; lane_index += 1) {
        const lane_sections = group_sections_by_lane_index.get(lane_index) || [];

        if(lane_sections.length === 0) {
          render_items.push({
            type: 'placeholder',
            key: `${group_key}-lane-${lane_index}`,
          });
          continue;
        }

        lane_sections.forEach(section_info => {
          render_items.push({
            type: 'section',
            section_info,
          });
        });
      }
    });

  unaligned_sections.forEach(section_info => {
    render_items.push({
      type: 'section',
      section_info,
    });
  });

  return render_items;
};

export const getSectionAlignmentTemplateFromSections = (sections = []) => {
  const lanes_by_key = new Map();

  (Array.isArray(sections) ? sections : []).forEach(section_info => {
    const lane_info = getSectionAlignmentLaneInfo(section_info);
    if(lane_info) lanes_by_key.set(lane_info.key, lane_info);
  });

  const build_group = (lanes) => lanes
    .filter(lane => lanes_by_key.has(lane.key))
    .map(lane => lanes_by_key.get(lane.key));

  return {
    full_year: build_group(SECTION_ALIGNMENT_LANES.full_year),
    semester: build_group(SECTION_ALIGNMENT_LANES.semester),
    quarter: build_group(SECTION_ALIGNMENT_LANES.quarter),
    cycle: build_group(SECTION_ALIGNMENT_LANES.cycle),
  };
};

export const getTermFilterOptionsFromSections = (sections = [], { includeUnplaced = true } = {}) => {
  const option_map = new Map();
  if(includeUnplaced) option_map.set(UNPLACED_TERM_VALUE, { value: UNPLACED_TERM_VALUE, display: 'Unplaced' });

  TERM_FILTER_PRESETS.forEach(term_preset => {
    option_map.set(term_preset.value, { value: term_preset.value, display: term_preset.display });
  });

  (Array.isArray(sections) ? sections : []).forEach(section_info => {
    const term_filter_info = getSectionTermFilterInfo(section_info);
    if(term_filter_info.value === UNPLACED_TERM_VALUE) return;
    option_map.set(term_filter_info.value, {
      value: term_filter_info.value,
      display: term_filter_info.display,
    });
  });

  return Array.from(option_map.values());
};
